/**
 * AuthModal - Intent-First Signup Modal
 * 
 * Shows a focused signup modal with Google OAuth
 * Stores intent in localStorage so after OAuth return, user lands in their new journal
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { X } from "lucide-react";
import { Link } from "react-router-dom";

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialMode?: 'login' | 'signup';
}

export const AuthModal = ({ isOpen, onClose, initialMode = 'signup' }: AuthModalProps) => {
    const { signInWithGoogle } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [mode, setMode] = useState<'login' | 'signup'>(initialMode);

    // Sync mode with initialMode prop whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            setMode(initialMode);
        }
    }, [isOpen, initialMode]);

    if (!isOpen) return null;

    const handleGoogleAuth = async () => {
        setIsLoading(true);

        // Store intent only if in signup mode
        if (mode === 'signup') {
            localStorage.setItem('pendingAction', 'create_journal');
        } else {
            localStorage.removeItem('pendingAction');
        }

        try {
            await signInWithGoogle();
        } catch (error) {
            console.error(`${mode} failed:`, error);
            localStorage.removeItem('pendingAction');
            setIsLoading(false);
        }
    };

    const isSignUp = mode === 'signup';

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[60] animate-fade-in"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 pointer-events-auto animate-scale-in relative border border-slate-100"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-all"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    {/* Content */}
                    <div className="text-center space-y-8">
                        {/* Logo */}
                        <div className="pt-2">
                            <img
                                src="/Logo/sblogo.png"
                                alt="StudiBudi"
                                className="h-10 w-auto mx-auto"
                            />
                        </div>

                        {/* Headline */}
                        <div className="space-y-3">
                            <h2 className="text-2xl font-semibold text-slate-800 tracking-tight">
                                {isSignUp ? "Start learning smarter" : "Welcome back"}
                            </h2>
                            <p className="text-sm text-slate-500 leading-relaxed px-4">
                                {isSignUp
                                    ? "Create your free account with Google to begin your knowledge trail"
                                    : "Log in to your account with Google to continue your journey"}
                            </p>
                        </div>

                        {/* Google Auth Button */}
                        <Button
                            variant="outline"
                            className="w-full h-14 border-2 border-slate-100 hover:border-slate-200 hover:bg-slate-50 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all text-base font-semibold text-slate-700"
                            onClick={handleGoogleAuth}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3" />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    {isSignUp ? "Continue with Google" : "Log in with Google"}
                                </>
                            )}
                        </Button>

                        {/* Toggle link */}
                        <div className="pt-6 border-t border-slate-100/80">
                            <p className="text-sm text-slate-500">
                                {isSignUp ? "Already have an account?" : "New to StudiBudi?"}{' '}
                                <button
                                    onClick={() => setMode(isSignUp ? 'login' : 'signup')}
                                    className="text-blue-600 hover:text-blue-700 font-semibold hover:underline decoration-2 underline-offset-4 transition-all"
                                >
                                    {isSignUp ? "Log in" : "Sign up for free"}
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AuthModal;
